import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { logger } from "@/lib/default-logger";

export interface IntakeKey {
	id: string;
	name: string;
	campaignId: string;
	/** Public identifier the website sends in X-AX-Key. Safe to display. */
	keyId: string;
	allowedIps: string[];
	allowedCountries: string[];
	defaultCountry: string | null;
	fieldMap: Record<string, string>;
	tags: string[];
	rateLimitPerHour: number;
	cooldownSeconds: number;
	enabled: boolean;
	expiresAt: string | null;
	lastUsedAt: string | null;
	createdAt: string;
	revoked: boolean;
	expired: boolean;
}

/** Returned only on create/rotate - the secret is never readable again. */
export interface IntakeKeySecret {
	key: IntakeKey;
	secret: string;
	keyHeader: string;
	notice: string;
}

export type IntakeRequestStatus = "queued" | "duplicate" | "rejected";

export interface IntakeRequest {
	id: string;
	keyId: string;
	campaignId: string;
	contactId: string | null;
	leadId: string | null;
	status: IntakeRequestStatus;
	reason: string | null;
	phone: string | null;
	name: string | null;
	source: string | null;
	message: string | null;
	consented: boolean;
	createdAt: string;
}

export interface CampaignReadiness {
	ready: boolean;
	problems: string[];
}

export const intakeKeysKeys = {
	all: ["intakeKeys"] as const,
	lists: () => [...intakeKeysKeys.all, "list"] as const,
	requests: () => [...intakeKeysKeys.all, "requests"] as const,
	readiness: (campaignId: string) => [...intakeKeysKeys.all, "readiness", campaignId] as const,
};

function errorMessage(error: any, fallback: string): string {
	const message = error?.response?.data?.message ?? error?.response?.data?.errors;
	if (Array.isArray(message)) return message.join(" ");
	return message || fallback;
}

/**
 * Whether a campaign can back a callback form. Surfaced before the key is
 * created so the operator sees "group is filter-based" up front rather than as
 * a submit error.
 */
export function useCampaignReadiness(campaignId: string | null) {
	return useQuery({
		queryKey: intakeKeysKeys.readiness(campaignId ?? "none"),
		queryFn: async (): Promise<CampaignReadiness> => {
			const response = await apiClient.get(`/intake-keys/campaign-readiness/${campaignId}`);
			return response.data;
		},
		enabled: Boolean(campaignId),
		retry: false,
	});
}

export function useCreateIntakeKey() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ data }: { data: any }): Promise<IntakeKeySecret> => {
			const response = await apiClient.post(`/intake-keys`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: intakeKeysKeys.lists() });
			toast.success("Intake key created");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not create the intake key"));
		},
	});
}

export function useUpdateIntakeKey() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: any }) => {
			const response = await apiClient.put(`/intake-keys/${id}`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: intakeKeysKeys.lists() });
			toast.success("Intake key updated");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not update the intake key"));
		},
	});
}

export function useRotateIntakeKeySecret() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<IntakeKeySecret> => {
			const response = await apiClient.post(`/intake-keys/${id}/rotate`);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: intakeKeysKeys.lists() });
			toast.success("New signing secret issued");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not rotate the signing secret"));
		},
	});
}

export function useRevokeIntakeKey() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient.delete(`/intake-keys/${id}`);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: intakeKeysKeys.lists() });
			toast.success("Intake key revoked");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not revoke the intake key"));
		},
	});
}
